import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, Mail, MapPin, Clock, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import contactHero from "@/assets/contact-hero.jpg";
import contactHeroVideo from "@/assets/contact-hero.mp4.asset.json";
import ContactForm from "@/components/ContactForm";
import DigitalBusinessCard from "@/components/DigitalBusinessCard";
import chauffeurCta from "@/assets/chauffeur-cta.webp";
const Contact = () => {
  return <div className="min-h-screen">
      <SEO 
        title="Contact Rick's Black Car Service | Call 210-296-7409"
        description="Contact Rick's Black Car Service for luxury transportation in San Antonio. Call 210-296-7409 or book online. Available 24/7!"
        canonicalPath="/contact"
        schema={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://ricksblackcarservicesantonio.com/#business",
          "name": "Rick's Black Car Service",
          "url": "https://ricksblackcarservicesantonio.com",
          "logo": "https://ricksblackcarservicesantonio.com/og-image.jpg",
          "image": "https://ricksblackcarservicesantonio.com/og-image.jpg",
          "telephone": "+1-210-296-7409",
          "email": "ricksblackcarservice@outlook.com",
          "priceRange": "$$",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "16034 Rough Oak Street",
            "addressLocality": "San Antonio",
            "addressRegion": "TX",
            "postalCode": "78232",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 29.5850,
            "longitude": -98.4615
          },
          "openingHoursSpecification": [{
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "00:00",
            "closes": "23:59"
          }],
          "areaServed": {
            "@type": "City",
            "name": "San Antonio"
          }
        }}
      />
      <Navbar />
      <main className="pt-20 overflow-hidden">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <video
            className="absolute inset-0 w-full h-full object-cover opacity-70"
            src={contactHeroVideo.url}
            poster={contactHero}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
           disableRemotePlayback/>
          <div className="absolute inset-0 bg-gradient-to-r from-background/20 via-background/10 to-background/20" />
          <div className="container mx-auto px-4 relative z-10 overflow-hidden">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">Contact Us</span>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground break-words">
                Contact Rick's Black Car Service
                <span className="block text-xl sm:text-2xl md:text-3xl mt-2 text-primary">Book Your Luxury Ride</span>
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
                Ready to experience luxury transportation in San Antonio? Contact Rick's Black Car Service today to book your ride or request a quote. We're available 24/7 for all your transportation needs.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Information & Form */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl font-serif font-bold text-foreground mb-6">
                    Contact Information
                  </h2>
                  <p className="text-muted-foreground text-lg mb-4">
                    We're here to assist you 24/7. Reach out to us through any of the following channels.
                  </p>
                  
                  <div className="bg-secondary p-6 rounded-lg space-y-3">
                    <h3 className="text-xl font-semibold text-foreground">How to Book</h3>
                    <ol className="text-muted-foreground space-y-2 list-decimal list-inside">
                      <li><strong>Call or Text (210) 296-7409</strong> - Fastest way, available 24/7</li>
                      <li><strong>Email</strong> ricksblackcarservice@outlook.com</li>
                      <li><strong>Online Form</strong> - Complete booking form below</li>
                    </ol>
                  </div>

                  <div className="bg-secondary p-6 rounded-lg space-y-3 mt-4">
                    <h3 className="text-xl font-semibold text-foreground">What to Include</h3>
                    <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                      <li>Service type (airport, corporate, event, hourly, wine tour)</li>
                      <li>Date and time</li>
                      <li>Pickup location and destination</li>
                      <li>Number of passengers</li>
                      <li>Flight number (for airport service)</li>
                      <li>Special requests</li>
                    </ul>
                  </div>

                  <div className="bg-primary/10 p-6 rounded-lg space-y-3 mt-4 border border-primary/20">
                    <h3 className="text-xl font-semibold text-foreground">Why Choose Rick's</h3>
                    <ul className="text-muted-foreground space-y-1">
                      <li>✓ 5+ years professional experience</li>
                      <li>✓ Boutique, personalized service</li>
                      <li>✓ Pristine 2025 Audi Q7</li>
                      <li>✓ Available 24/7</li>
                      <li>✓ Transparent pricing</li>
                      <li>✓ Airport transfers starting at $85</li>
                    </ul>
                  </div>
                </div>

                <div className="space-y-6">
                  <Card className="card-luxury">
                    <CardContent className="p-6 flex items-start space-x-4">
                      <div className="text-primary mt-1">
                        <Phone className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground mb-2">Phone</h3>
                        <a href="tel:+12102967409" className="text-muted-foreground hover:text-primary transition-colors">
                          210-296-7409
                        </a>
                        
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-6 flex items-start space-x-4">
                      <div className="text-primary mt-1">
                        <Mail className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground mb-2">Email</h3>
                        <a href="mailto:ricksblackcarservice@outlook.com" className="text-muted-foreground hover:text-primary transition-colors">
                          ricksblackcarservice@outlook.com
                        </a>
                        <p className="text-muted-foreground text-sm mt-1">We'll respond within 24 hours</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-6 flex items-start space-x-4">
                      <div className="text-primary mt-1">
                        <MapPin className="w-6 h-6" />
                      </div>
                       <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground mb-2">Address</h3>
                        <p className="text-muted-foreground">
                          16034 Rough Oak Street, San Antonio, TX 78232
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                   <Card className="card-luxury">
                    <CardContent className="p-6 flex items-start space-x-4">
                      <div className="text-primary mt-1">
                        <Clock className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground mb-2">Business Hours</h3>
                        <p className="text-muted-foreground">
                          Available 24/7
                          <br />
                          Including weekends and holidays
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury card-orange-glow border-primary/20">
                    <CardContent className="p-6 flex items-start space-x-4">
                      <div className="text-primary mt-1">
                        <HelpCircle className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground mb-2">Common questions?</h3>
                        <Link to="/faq" className="text-primary hover:text-primary/80 transition-colors underline decoration-primary/50">
                          See our FAQ
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Contact Form */}
              <Card className="card-luxury">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-serif font-bold text-foreground mb-6">Send us a Message</h2>
                  <p className="text-muted-foreground mb-6">
                    We're here to help! Contact us today.
                  </p>
                  <ContactForm />
                  <div className="mt-8 pt-6 border-t border-border/30 text-center space-y-2">
                    <p className="text-muted-foreground">Prefer to call?</p>
                    <a href="tel:+12102967409" className="text-primary text-xl font-semibold hover:text-primary/80 transition-colors block">
                      Call: (210) 296-7409
                    </a>
                    <a href="mailto:ricksblackcarservice@outlook.com" className="text-primary hover:text-primary/80 transition-colors block">
                      Email: ricksblackcarservice@outlook.com
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas Section */}
        <section className="py-20 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-4xl font-serif font-bold text-foreground mb-4">
                Service Areas
              </h2>
              <p className="text-lg text-muted-foreground">
                We proudly serve San Antonio and surrounding areas including:
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {[
                'San Antonio (all neighborhoods)',
                'Stone Oak',
                'Alamo Heights',
                'The Dominion',
                'Medical Center',
                'Downtown San Antonio',
                'Boerne',
                'New Braunfels',
                'Schertz',
                'Cibolo',
                'Selma',
                'Universal City',
                'Live Oak',
                'Converse',
                'Austin (long-distance)',
                'Fredericksburg (wine tours)',
                'Texas Hill Country',
              ].map((area) => (
                <div key={area} className="bg-background/80 backdrop-blur-sm p-4 rounded-lg border border-border/50 text-center">
                  <p className="text-foreground font-medium">{area}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Book With Us Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-4xl font-serif font-bold text-foreground mb-4">
                Why Book With Us
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {[
                { title: '24/7 Availability', description: 'Book anytime, day or night' },
                { title: 'Instant Quotes', description: 'Call for immediate pricing' },
                { title: 'Easy Booking', description: 'Phone, email, or online' },
                { title: 'Flexible Scheduling', description: 'Accommodate your timeline' },
                { title: 'Professional Service', description: 'Courteous, reliable chauffeurs' },
                { title: 'Luxury Fleet', description: 'Immaculate vehicles' },
                { title: 'Competitive Rates', description: 'No hidden fees' },
                { title: 'Corporate Accounts', description: 'Simplified billing for businesses' },
                { title: 'Last-Minute Bookings', description: 'We accommodate urgent requests' },
                { title: 'Satisfaction Guaranteed', description: 'Your experience is our priority' },
              ].map((item) => (
                <Card key={item.title} className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-serif font-bold text-foreground mb-12 text-center">
                Frequently Asked Questions
              </h2>
              <div className="space-y-6">
                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      How do I book a ride?
                    </h3>
                    <p className="text-muted-foreground">
                      Call 210-296-7409, email ricksblackcarservice@outlook.com, or fill out the contact form above. We'll respond promptly with availability and pricing.
                    </p>
                  </CardContent>
                </Card>

                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      How far in advance should I book?
                    </h3>
                    <p className="text-muted-foreground">
                      We recommend booking as early as possible, especially for weddings and special events. However, we accommodate last-minute requests based on availability.
                    </p>
                  </CardContent>
                </Card>

                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      Do you offer instant quotes?
                    </h3>
                    <p className="text-muted-foreground">
                      Yes! Call 210-296-7409 for immediate pricing based on your specific transportation needs.
                    </p>
                  </CardContent>
                </Card>

                <Card className="card-luxury">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      What payment methods do you accept?
                    </h3>
                    <p className="text-muted-foreground">
                      We accept cash, all major credit cards, and offer corporate account billing for business clients.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${chauffeurCta})` }} />
          <div className="absolute inset-0 bg-gradient-to-br from-background/70 via-background/60 to-background/70" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground">
                Don't Wait - Book Your Luxury Transportation Now!
              </h2>
              <p className="text-xl text-muted-foreground">
                Call 210-296-7409 or email ricksblackcarservice@outlook.com for instant quotes and reservations.
              </p>
              <p className="text-2xl font-semibold text-primary">
                Experience the Rick's Black Car Service difference today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <Button asChild size="lg" className="btn-luxury text-lg px-8 py-6">
                  <a href="tel:+12102967409">Call Now: 210-296-7409</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-2">
                  <a href="mailto:ricksblackcarservice@outlook.com">Send Email</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA variant="banner" />
        
      </main>
      <Footer />
    </div>;
};
export default Contact;